import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Pencil, Trash2, Plus, ArrowLeft, Loader2, Power, PowerOff } from 'lucide-react';
import {
  fetchProducts,
  createProduct,
  updateProductApi,
  deleteProductApi,
  fetchOrders,
  getMaintenanceStatus,
  toggleMaintenance,
} from '../api';
import { useToast } from '../hooks/use-toast';

export const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('products'); // products | orders
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintToggling, setMaintToggling] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    image: '',
    back_image: '',
    description: '',
    sizes: '',
    inStock: true,
  });

  useEffect(() => {
    loadData();
    loadMaintenance();
  }, [activeTab]);

  const loadMaintenance = async () => {
    try {
      const s = await getMaintenanceStatus();
      setMaintenanceMode(s.enabled);
      setMaintenanceMessage(s.message || '');
    } catch (err) {
      console.error('Could not load maintenance status', err);
    }
  };

  const handleToggleMaintenance = async () => {
    setMaintToggling(true);
    try {
      const newState = !maintenanceMode;
      await toggleMaintenance(newState, maintenanceMessage || null);
      setMaintenanceMode(newState);
      toast({
        title: newState ? '🔒 Store paused' : '🟢 Store live',
        description: newState
          ? 'Visitors will see the maintenance page'
          : 'Your store is back online',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Could not toggle maintenance',
        variant: 'destructive',
      });
    } finally {
      setMaintToggling(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'products') {
        const data = await fetchProducts();
        setProducts(data);
      } else {
        const data = await fetchOrders();
        setOrders(data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast({
        title: 'Error',
        description: 'Failed to load data from server',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      category: '',
      image: '',
      back_image: '',
      description: '',
      sizes: '',
      inStock: true,
    });
    setEditingProduct(null);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        price: product.price.toString(),
        category: product.category,
        image: product.image,
        back_image: product.back_image || '',
        description: product.description,
        sizes: product.sizes.join(', '),
        inStock: product.inStock,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate image URL
    const imageUrl = formData.image.trim();
    if (!/^https?:\/\/.+/i.test(imageUrl)) {
      toast({
        title: 'Invalid Image URL',
        description: 'Image URL must start with http:// or https://',
        variant: 'destructive',
      });
      return;
    }

    // Validate back image URL if provided
    const backImageUrl = formData.back_image.trim();
    if (backImageUrl && !/^https?:\/\/.+/i.test(backImageUrl)) {
      toast({
        title: 'Invalid Back Image URL',
        description: 'Back image URL must start with http:// or https://',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    const productData = {
      name: formData.name,
      price: parseFloat(formData.price),
      category: formData.category,
      image: imageUrl,
      back_image: backImageUrl || null,
      description: formData.description,
      sizes: formData.sizes.split(',').map(s => s.trim()).filter(Boolean),
      inStock: formData.inStock,
    };

    try {
      if (editingProduct) {
        await updateProductApi(editingProduct.id, productData);
        toast({
          title: 'Product updated!',
          description: `${productData.name} has been updated`,
        });
      } else {
        await createProduct(productData);
        toast({
          title: 'Product added!',
          description: `${productData.name} has been added to the store`,
        });
      }

      await loadData();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Save failed:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to save product',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete ${product.name}?`)) return;

    try {
      await deleteProductApi(product.id);
      await loadData();
      toast({
        title: 'Product deleted',
        description: `${product.name} has been removed`,
      });
    } catch (err) {
      console.error('Delete failed:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-[8%]">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-10 flex-wrap gap-4">
          <div>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="mb-4 text-white hover:text-[#ff1f4d]"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="mr-2" /> Back to Store
            </Button>
            <h1 className="text-5xl font-black text-[#ff1f4d]">Admin Panel</h1>
            <p className="text-gray-400 mt-2">Manage your store</p>
          </div>

          {activeTab === 'products' && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-[#ff1f4d] hover:bg-white hover:text-black font-bold"
              data-testid="add-product-btn"
            >
              <Plus className="mr-2" /> Add Product
            </Button>
          )}
        </div>

        {/* Maintenance Mode Toggle */}
        <Card className={`mb-6 p-5 border-2 ${
          maintenanceMode
            ? 'bg-yellow-900/20 border-yellow-700/50'
            : 'bg-[#111] border-[#222]'
        }`} data-testid="maintenance-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {maintenanceMode ? (
                <PowerOff className="h-8 w-8 text-yellow-500" />
              ) : (
                <Power className="h-8 w-8 text-green-500" />
              )}
              <div>
                <h3 className="text-white font-bold text-lg">
                  {maintenanceMode ? 'Store is PAUSED' : 'Store is LIVE'}
                </h3>
                <p className="text-gray-400 text-sm">
                  {maintenanceMode
                    ? 'Visitors see the maintenance page. Checkout is blocked.'
                    : 'Customers can browse and buy.'}
                </p>
              </div>
            </div>
            <Button
              onClick={handleToggleMaintenance}
              disabled={maintToggling}
              className={`font-bold ${
                maintenanceMode
                  ? 'bg-green-600 hover:bg-green-500 text-white'
                  : 'bg-yellow-500 hover:bg-yellow-400 text-black'
              }`}
              data-testid="toggle-maintenance-btn"
            >
              {maintToggling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : maintenanceMode ? (
                'Resume Store'
              ) : (
                'Pause Store'
              )}
            </Button>
          </div>
          <div className="mt-4">
            <Input
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              placeholder="Optional message shown to visitors (e.g. 'New drop incoming Tuesday 7pm EST')"
              className="bg-black border-[#222] text-white"
              data-testid="maintenance-message-input"
            />
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#222]">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 font-bold transition-colors ${
              activeTab === 'products'
                ? 'text-[#ff1f4d] border-b-2 border-[#ff1f4d]'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-products"
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 font-bold transition-colors ${
              activeTab === 'orders'
                ? 'text-[#ff1f4d] border-b-2 border-[#ff1f4d]'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-orders"
          >
            Orders
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-[#ff1f4d]" />
          </div>
        ) : activeTab === 'products' ? (
          <Card className="bg-[#111] border-[#222]" data-testid="products-table">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#222] hover:bg-transparent">
                    <TableHead className="text-gray-400">Image</TableHead>
                    <TableHead className="text-gray-400">Name</TableHead>
                    <TableHead className="text-gray-400">Category</TableHead>
                    <TableHead className="text-gray-400">Price</TableHead>
                    <TableHead className="text-gray-400">Stock</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map(product => (
                    <TableRow
                      key={product.id}
                      className="border-[#222] hover:bg-[#1a1a1a]"
                      data-testid={`product-row-${product.id}`}
                    >
                      <TableCell>
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                      </TableCell>
                      <TableCell className="text-white font-medium">{product.name}</TableCell>
                      <TableCell className="text-gray-400 capitalize">{product.category}</TableCell>
                      <TableCell className="text-[#ff1f4d] font-bold">${product.price}</TableCell>
                      <TableCell>
                        <span
                          className={`text-sm px-2 py-1 rounded ${
                            product.inStock
                              ? 'bg-green-900/30 text-green-400'
                              : 'bg-red-900/30 text-red-400'
                          }`}
                        >
                          {product.inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => handleOpenDialog(product)}
                            variant="ghost"
                            size="icon"
                            className="text-gray-400 hover:text-[#ff1f4d]"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(product)}
                            variant="ghost"
                            size="icon"
                            className="text-gray-400 hover:text-red-500"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : (
          <Card className="bg-[#111] border-[#222]" data-testid="orders-table">
            {orders.length === 0 ? (
              <p className="text-center text-gray-500 py-12">No orders yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#222] hover:bg-transparent">
                      <TableHead className="text-gray-400">Order ID</TableHead>
                      <TableHead className="text-gray-400">Email</TableHead>
                      <TableHead className="text-gray-400">Items</TableHead>
                      <TableHead className="text-gray-400">Total</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Payment</TableHead>
                      <TableHead className="text-gray-400">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map(order => (
                      <TableRow
                        key={order.id}
                        className="border-[#222] hover:bg-[#1a1a1a]"
                        data-testid={`order-row-${order.id}`}
                      >
                        <TableCell className="text-white font-mono text-xs">{order.id}</TableCell>
                        <TableCell className="text-gray-300 text-sm">
                          {order.customer_email || '—'}
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {order.items?.length || 0} item(s)
                        </TableCell>
                        <TableCell className="text-[#ff1f4d] font-bold">
                          ${order.total?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              order.status === 'completed'
                                ? 'bg-green-900/30 text-green-400'
                                : order.status === 'cancelled'
                                ? 'bg-red-900/30 text-red-400'
                                : 'bg-yellow-900/30 text-yellow-400'
                            }`}
                          >
                            {order.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              order.payment_status === 'paid'
                                ? 'bg-green-900/30 text-green-400'
                                : 'bg-gray-800 text-gray-400'
                            }`}
                          >
                            {order.payment_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {order.created_at
                            ? new Date(order.created_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Add/Edit Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="product-form">
            <div>
              <Label htmlFor="name">Product Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-black border-[#222] text-white mt-1"
                required
                data-testid="product-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="bg-black border-[#222] text-white mt-1"
                  required
                  data-testid="product-price-input"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  required
                >
                  <SelectTrigger
                    className="bg-black border-[#222] text-white mt-1"
                    data-testid="product-category-select"
                  >
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#222] text-white">
                    <SelectItem value="jackets">Jackets</SelectItem>
                    <SelectItem value="pants">Pants</SelectItem>
                    <SelectItem value="shorts">Shorts</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="image">Front Image URL</Label>
              <Input
                id="image"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="bg-black border-[#222] text-white mt-1"
                placeholder="https://..."
                required
                data-testid="product-image-input"
              />
            </div>

            <div>
              <Label htmlFor="back_image">
                Back Image URL <span className="text-gray-500 text-xs">(optional — enables flip view)</span>
              </Label>
              <Input
                id="back_image"
                value={formData.back_image}
                onChange={(e) => setFormData({ ...formData, back_image: e.target.value })}
                className="bg-black border-[#222] text-white mt-1"
                placeholder="https://..."
                data-testid="product-back-image-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-black border-[#222] text-white mt-1"
                rows={3}
                required
                data-testid="product-description-input"
              />
            </div>

            <div>
              <Label htmlFor="sizes">Sizes (comma-separated)</Label>
              <Input
                id="sizes"
                value={formData.sizes}
                onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
                className="bg-black border-[#222] text-white mt-1"
                placeholder="S, M, L, XL or 28, 30, 32, 34"
                required
                data-testid="product-sizes-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="inStock"
                type="checkbox"
                checked={formData.inStock}
                onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                className="w-4 h-4"
                data-testid="product-instock-checkbox"
              />
              <Label htmlFor="inStock" className="cursor-pointer">In Stock</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
                className="flex-1 bg-transparent border-[#222] text-white hover:bg-[#222]"
                data-testid="cancel-product-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#ff1f4d] hover:bg-white hover:text-black font-bold disabled:opacity-50"
                data-testid="submit-product-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : editingProduct ? (
                  'Update Product'
                ) : (
                  'Add Product'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
